import { ButtonV2, ButtonDropdown, Input, Spinner } from '@cimplico/components';
import { ArrowSmallLeftIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toastNotification } from '../../components/toast/Toast';
import { useCreateJournal } from '../../queries/use-create-journal';
import {
    Journal,
    JournalStatusEnum,
    NewOrEditJournalFields,
    JournalLinesFields,
    UpdateJournalReq,
    UpdateJournalLineReq,
    JournalTaxTypeEnum,
} from '../../types/journal';
import { formatDateNumeric } from '../../utilities/formatting';
import { WorksheetWithAccount } from '../../types/worksheet';
import { WorkpaperRoleEnum, WorkpaperSourceEnum } from '../../types/workpaper';
import { isAllNullOrUndefined } from '../new-workpaper/utils';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useWorkpaper } from '../../queries/use-workpaper';
import { useEffect, useState } from 'react';
import { useUpdateJournals } from '../../queries/use-update-journals';
import {
    calcWorkpaperPeriod,
    calcJournalTotal,
    formatDefaultJournal,
    formatExistingJournalFormData,
    formatNewJournalFormData,
    formatUpdatedJournalLines,
    roundToTwoDecimal,
    validateExistingJournal,
    validateNewJournal,
} from './utils';
import NewOrEditJournalTable from './NewOrEditJournalTable';
import JournalStatusBadge from '../../components/journal-status-badge/JournalStatusBadge';
import JournalDetails from '../journal-overview/JournalDetails';
import JournalTaxTypeSelect from './JournalTaxTypeSelect';
import { useEntityAccounts } from '../../queries/use-coa';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

type NewOrEditJournalFormProps = {
    defaultJournal?: Journal;
    worksheet?: WorksheetWithAccount;
    useSmallerStyling?: boolean;
    onReturn: () => void;
    onCancel: () => void;
    onCreate?: (journalId: string) => void;
    onEdit?: (journalId: string) => void;
    onXeroError: (error?: { name: string; message: string }) => void;
};

function NewOrEditJournalForm({
    defaultJournal,
    worksheet,
    useSmallerStyling = false,
    onReturn,
    onCancel,
    onCreate,
    onEdit,
    onXeroError,
}: NewOrEditJournalFormProps): JSX.Element {
    const { trackEvent } = useIntercom();
    const createJournalQuery = useCreateJournal(worksheet?.id);
    const updateJournalQuery = useUpdateJournals(worksheet?.id, defaultJournal?.id);
    const { entity, id: workpaperId, provider, role, permissions } = useWorkpaperContext();
    const accountsQuery = useEntityAccounts(entity.id, entity.charts.find((c) => c.provider?.toString() === provider?.toString())!.id, {
        filter: { canJournal: true },
        sort: {
            dir: 'asc',
            field: 'code',
        },
    });

    const [narration, setNarration] = useState<string | undefined>(defaultJournal?.narration);
    const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});

    const getWorkpaperQuery = useWorkpaper(workpaperId);

    const roleCanPost =
        role === WorkpaperRoleEnum.REVIEWER ||
        role === WorkpaperRoleEnum.APPROVER ||
        (WorkpaperRoleEnum.PREPARER && permissions.preparer.canPostJournal);

    useEffect(() => {
        if (getWorkpaperQuery.data?.workpaper) {
            const { periodEnd, entity } = getWorkpaperQuery.data?.workpaper;
            const range = calcWorkpaperPeriod(new Date(periodEnd), entity.financialYearEndMonth, entity.financialYearEndDay);
            setDateRange(range);
        }
    }, [getWorkpaperQuery.data?.workpaper.periodEnd]);

    const emptyLines: JournalLinesFields[] = [
        { id: 'row-0', description: null, account: null, debit: null, credit: null, amountEditable: true, taxRate: null },
        {
            id: 'row-1',
            description: null,
            account: worksheet?.account!.entityAccountId || null,
            debit: null,
            credit: null,
            amountEditable: worksheet === undefined,
            taxRate: null,
        },
    ];

    const defaultValues: NewOrEditJournalFields = formatDefaultJournal(emptyLines, defaultJournal);
    const methods = useForm<NewOrEditJournalFields>({
        defaultValues: defaultJournal
            ? defaultValues
            : {
                  date: formatDateNumeric(getWorkpaperQuery.data!.workpaper!.periodEnd, true),
                  lines: emptyLines,
                  taxType: JournalTaxTypeEnum.NO_TAX,
              },
    });

    function updateJournalTotal(newTaxType: JournalTaxTypeEnum): void {
        const total = calcJournalTotal(newTaxType, methods.getValues('lines'));

        methods.setValue('debitTotal', total.debitTotal);
        methods.setValue('creditTotal', total.creditTotal);
        methods.setValue('debitSubtotal', total.debitSubtotal);
        methods.setValue('creditSubtotal', total.creditSubtotal);
        methods.setValue('debitTaxTotal', total.debitTaxTotal);
        methods.setValue('creditTaxTotal', total.creditTaxTotal);
        methods.setValue('valueOutBy', roundToTwoDecimal({ amount: total.debitTotal - total.creditTotal }));
    }

    if (accountsQuery.isLoading || getWorkpaperQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    // Create posted or draft journal
    function handleCreate(status: JournalStatusEnum): void {
        const values = methods.getValues();
        const journalToCreate = formatNewJournalFormData(values, status);
        const validationError = validateNewJournal(journalToCreate);

        if (validationError) {
            toastNotification('error', 'Unable to create a journal!', validationError);
            return;
        }

        createJournalQuery.mutate(
            { journal: journalToCreate },
            {
                onSuccess: (res) => {
                    onCreate && onCreate(res.journal.id);
                    toastNotification('success', 'New journal created!');
                    trackEvent(intercomEvents.journal.created.event);
                },
                onError: (res) => {
                    const draftJournal = res.response?.data?.draftJournal;
                    const error = res.response?.data.error;
                    const message = res.response?.data.message;

                    if (error && message) {
                        onXeroError({ message, name: error });
                    }

                    if (draftJournal?.id) {
                        onCreate && onCreate(draftJournal.id);
                        toastNotification('success', 'New draft journal created!');
                        toastNotification('error', 'Unable to post journal!', message);
                    } else {
                        toastNotification('error', 'Unable to create new journal!', message);
                    }
                },
            }
        );
    }

    // Update draft journal details and optionally post it
    function handleEdit(status: JournalStatusEnum): void {
        const values = methods.getValues();

        if (!defaultJournal) {
            onCancel();
            return;
        }

        const journalToUpdate = formatExistingJournalFormData(values, status, defaultJournal.id);
        const validationError = validateExistingJournal(journalToUpdate);

        if (validationError) {
            toastNotification('error', 'Unable to update a journal!', validationError);
            return;
        }

        // Find journal lines that have been removed
        let removedLinesField: UpdateJournalLineReq[] = [];
        if (journalToUpdate.lines) {
            const updatedJournalLines = journalToUpdate.lines.map((l) => l.id).filter((id) => id !== undefined) as string[];
            const removedLineIds = defaultJournal.lines.filter((l) => !updatedJournalLines.includes(l.id)).map((line) => line.id);

            removedLinesField = removedLineIds.map((l) => {
                return { id: l, fields: null };
            });
        }

        // Format journal data line object and passed in changed fields only
        const updatedJournalLines: UpdateJournalLineReq[] | undefined = formatUpdatedJournalLines(defaultValues, journalToUpdate);
        const updatedJournal: UpdateJournalReq = {
            id: defaultJournal.id,
            status,
            date: defaultValues.date === values.date ? undefined : values.date,
            narration: defaultValues.narration === values.narration ? undefined : values.narration,
            taxType:
                defaultValues.taxType === (values.taxType || JournalTaxTypeEnum.NO_TAX)
                    ? undefined
                    : values.taxType || JournalTaxTypeEnum.NO_TAX,
            lines: updatedJournalLines ? updatedJournalLines.concat(removedLinesField) : removedLinesField,
        };

        // If no fields have been updated, close dialog
        if (
            status === defaultJournal.status &&
            isAllNullOrUndefined([updatedJournal.narration, updatedJournal.date, updatedJournal.taxType]) &&
            (updatedJournal.lines?.length === 0 ||
                updatedJournal.lines?.every(
                    (line) =>
                        line.fields !== null &&
                        isAllNullOrUndefined([
                            line.fields?.sequence,
                            line.fields?.account,
                            line.fields?.description,
                            line.fields?.lineAmount,
                            line.fields.taxRate,
                            line.fields.lineType,
                        ])
                ))
        ) {
            onEdit && onEdit(updatedJournal.id);
            return;
        }
        updateJournalQuery.mutate([updatedJournal], {
            onSuccess: () => {
                onEdit && onEdit(updatedJournal.id);
                toastNotification('success', 'Journal details updated!');
            },
            onError: (res) => {
                const results = res.response?.data.results;
                const errorResult = results.find((res: { statusCode: number }) => res.statusCode === 400);

                const error = errorResult.error.fields.error;
                const message = errorResult.error.fields.errorMessage;

                if (error && message) {
                    onXeroError({ message, name: error });
                }

                toastNotification('error', 'Unable to update journal!', message);
            },
        });
    }

    return (
        <FormProvider {...methods}>
            {(createJournalQuery.isLoading || updateJournalQuery.isLoading || getWorkpaperQuery.isLoading) && (
                <div className="pointer-events-none absolute top-0 left-0 w-full h-full z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-2 pb-4">
                <div className="flex gap-2 items-center text-gray-500 cursor-pointer w-fit" onClick={onReturn}>
                    <ArrowSmallLeftIcon className="h-5 w-5" />
                    <div className={`${useSmallerStyling ? 'text-xs' : 'text-sm'}`}>Back to journals</div>
                </div>
                <div className="flex justify-between items-center gap-4">
                    {defaultJournal ? (
                        <h1
                            className={`${
                                useSmallerStyling ? 'text-lg' : 'text-2xl'
                            } leading-none truncate text-gray-800 font-bold dark:text-white/[0.87] max-w-[50ch]`}
                        >
                            JNL#{defaultJournal.reference.toString().padStart(3, '0')}
                            {defaultJournal.narration ? ` - ${defaultJournal.narration}` : ''}
                        </h1>
                    ) : (
                        <div className="flex gap-2.5 items-center">
                            <h1
                                className={`${
                                    useSmallerStyling ? 'text-lg' : 'text-2xl'
                                } leading-none text-gray-800 font-bold dark:text-white/[0.87] max-w-[50ch]`}
                            >
                                New Journal
                            </h1>
                            <div className="grow-0">
                                <JournalStatusBadge status={JournalStatusEnum.DRAFT} />
                            </div>
                        </div>
                    )}
                    <div className="flex gap-4">
                        <ButtonV2
                            type="hollow"
                            label={defaultJournal ? 'Cancel' : 'Delete'}
                            size="xs"
                            leftIcon={defaultJournal ? undefined : <XMarkIcon />}
                            onClick={onCancel}
                        />
                        {roleCanPost ? (
                            <ButtonDropdown
                                variant="primary"
                                label="Save and Post"
                                size="xs"
                                onClick={(): void =>
                                    defaultJournal ? handleEdit(JournalStatusEnum.POSTED) : handleCreate(JournalStatusEnum.POSTED)
                                }
                                disabled={createJournalQuery.isLoading}
                            >
                                {(!defaultJournal || defaultJournal?.status !== JournalStatusEnum.POSTED) && (
                                    <ButtonDropdown.SecondaryAction
                                        label="Save as draft"
                                        onClick={(): void =>
                                            defaultJournal ? handleEdit(JournalStatusEnum.DRAFT) : handleCreate(JournalStatusEnum.DRAFT)
                                        }
                                    />
                                )}
                            </ButtonDropdown>
                        ) : (
                            <ButtonV2
                                label="Save as draft"
                                size="xs"
                                onClick={(): void =>
                                    defaultJournal ? handleEdit(JournalStatusEnum.DRAFT) : handleCreate(JournalStatusEnum.DRAFT)
                                }
                                isDisabled={createJournalQuery.isLoading}
                            />
                        )}
                    </div>
                </div>
                {defaultJournal && <JournalDetails showStatus journal={defaultJournal} />}
                <div className="grid grid-cols-2 items-center gap-2">
                    <div className="grid grid-cols-[0.1fr_1fr] gap-4 w-full pt-2.5">
                        <div className="w-[100px]">
                            <Controller
                                name="date"
                                control={methods.control}
                                render={({ field: { onChange, value } }): JSX.Element => {
                                    return (
                                        <Input
                                            type="date"
                                            placeholder="Date..."
                                            size="small"
                                            variant="underline"
                                            value={value || ''}
                                            max={dateRange.endDate}
                                            min={dateRange.startDate}
                                            onChange={(e): void => {
                                                onChange(e.target.value === '' ? null : formatDateNumeric(e.target.value, true));
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className="grow">
                            <Controller
                                name="narration"
                                control={methods.control}
                                render={({ field: { onChange, value } }): JSX.Element => (
                                    <Input
                                        type="text"
                                        variant="underline"
                                        size="small"
                                        placeholder="Narration..."
                                        value={value || ''}
                                        onChange={(e): void => onChange(e.target.value === '' ? null : e.target.value)}
                                        onBlur={(e): void => setNarration(e.target.value === '' ? undefined : e.target.value)}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {provider === WorkpaperSourceEnum.XERO && (
                        <div className="flex justify-end">
                            <Controller
                                name="taxType"
                                control={methods.control}
                                render={({ field: { onChange, value } }): JSX.Element => (
                                    <JournalTaxTypeSelect
                                        onChange={(type): void => {
                                            onChange(type);
                                            if (!type || type === JournalTaxTypeEnum.NO_TAX) {
                                                methods.setValue(
                                                    `lines`,
                                                    methods.getValues('lines').map((line) => ({ ...line, taxRate: null }))
                                                );
                                            }
                                            updateJournalTotal(type || JournalTaxTypeEnum.NO_TAX);
                                        }}
                                        value={value}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>
            </div>
            <NewOrEditJournalTable
                worksheet={worksheet}
                accounts={accountsQuery.data?.items}
                narration={narration}
                entityId={getWorkpaperQuery.data?.workpaper.entity.id as string}
            />
        </FormProvider>
    );
}

export default NewOrEditJournalForm;
